import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import {
  ArrowLeft,
  Search,
  MessageCircle,
  Send,
  Clock,
  Image as ImageIcon,
  X,
  Loader2,
  Plus,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { useToast } from "@/shared/hooks/use-toast";
import { useSubscriptionGuard } from "@/shared/hooks/useSubscriptionGuard";
import { SubscriptionRequiredScreen } from "@/features/billing/components/SubscriptionRequiredScreen";
import { useSupportTickets, useTicketChat, SupportTicket, SupportMessage } from "@/shared/hooks/useSupportTickets";
import { apiClient } from "@/shared/services/apiClient";
import { cn } from "@/lib/utils";

const CATEGORY_LABELS: Record<string, string> = {
  technical: "Technical Issue",
  billing: "Billing & Payment",
  feature: "Feature Request",
  account: "Account Management",
  other: "Other",
};

const STATUS_STYLES: Record<string, string> = {
  open: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
  pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  resolved: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  closed: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300",
};

export default function MessagesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { canAccess, reason } = useSubscriptionGuard();

  const { tickets, loading: ticketsLoading, refetch } = useSupportTickets();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState<'active' | 'closed'>('active');
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(
    searchParams.get("ticket")
  );

  // Update URL when ticket is selected
  useEffect(() => {
    if (selectedTicketId) {
      setSearchParams({ ticket: selectedTicketId });
    } else {
      setSearchParams({});
    }
  }, [selectedTicketId, setSearchParams]);

  // If navigated from a notification, auto-switch tab to find the ticket
  useEffect(() => {
    if (selectedTicketId && tickets.length > 0) {
      const ticket = tickets.find(t => t.ticket_id === selectedTicketId);
      if (ticket && (ticket.status === 'closed' || ticket.status === 'resolved')) {
        setActiveTab('closed');
      }
    }
  }, [selectedTicketId, tickets]);

  // Separate tickets into active and closed
  const activeTickets = tickets.filter(t => t.status !== 'closed' && t.status !== 'resolved');
  const closedTickets = tickets.filter(t => t.status === 'closed' || t.status === 'resolved');

  const displayTickets = activeTab === 'active' ? activeTickets : closedTickets;

  const filteredTickets = displayTickets.filter(ticket =>
    ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.ticket_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.message.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedTicket = tickets.find(t => t.ticket_id === selectedTicketId);

  // Check subscription access
  if (!canAccess) {
    return (
      <SubscriptionRequiredScreen
        feature="Messages"
        reason={reason}
        onUpgrade={() => navigate('/profile?tab=billing')}
      />
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Support Tickets</h1>
        <p className="text-muted-foreground">View and manage your support conversations</p>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => selectedTicketId ? setSelectedTicketId(null) : navigate('/dashboard')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {selectedTicketId ? "Back to Tickets" : "Back"}
        </Button>
        {!selectedTicketId && (
          <Button
            onClick={() => navigate('/support')}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            New Ticket
          </Button>
        )}
      </div>

      {selectedTicketId && selectedTicket ? (
        <TicketChatView
          ticket={selectedTicket}
          onClose={() => setSelectedTicketId(null)}
        />
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Your Tickets</CardTitle>
                <CardDescription>
                  {tickets.length} ticket{tickets.length !== 1 ? 's' : ''} total
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Tabs */}
            <div className="border-b border-border">
              <div className="flex gap-6">
                <button
                  onClick={() => setActiveTab('active')}
                  className={cn(
                    "pb-3 px-1 font-medium text-sm border-b-2 transition-colors",
                    activeTab === 'active'
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  )}
                >
                  Active ({activeTickets.length})
                </button>
                <button
                  onClick={() => setActiveTab('closed')}
                  className={cn(
                    "pb-3 px-1 font-medium text-sm border-b-2 transition-colors",
                    activeTab === 'closed'
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  )}
                >
                  Closed ({closedTickets.length})
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tickets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Tickets List */}
            <div className="space-y-3">
              {ticketsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : filteredTickets.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>
                    {searchTerm
                      ? "No tickets found matching your search."
                      : activeTab === 'active'
                        ? "No active support tickets."
                        : "No closed support tickets."}
                  </p>
                  {!searchTerm && activeTab === 'active' && (
                    <Button
                      variant="outline"
                      className="mt-4"
                      onClick={() => navigate('/support')}
                    >
                      Create Your First Ticket
                    </Button>
                  )}
                </div>
              ) : (
                filteredTickets.map((ticket) => {
                  const hasUnread = false; // TODO: Implement unread count from API
                  return (
                    <Card
                      key={ticket.id}
                      className={cn(
                        "cursor-pointer transition-colors hover:bg-accent/50",
                        hasUnread && "border-2 border-primary"
                      )}
                      onClick={() => setSelectedTicketId(ticket.ticket_id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-mono text-muted-foreground">
                                {ticket.ticket_id}
                              </span>
                              {hasUnread && (
                                <Badge variant="default" className="bg-primary text-primary-foreground text-xs px-2 py-0">
                                  New
                                </Badge>
                              )}
                              <Badge className={STATUS_STYLES[ticket.status]}>
                                {ticket.status}
                              </Badge>
                            </div>
                            <h3 className="font-medium truncate mb-1">
                              {ticket.subject}
                            </h3>
                            <p className="text-sm text-muted-foreground truncate mb-2">
                              {ticket.message}
                            </p>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {CATEGORY_LABELS[ticket.category] || ticket.category}
                              </span>
                              <span>
                                {format(new Date(ticket.updated_at), "MMM d, h:mm a")}
                              </span>
                            </div>
                          </div>
                          <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Ticket Chat View Component
function TicketChatView({ ticket, onClose }: { ticket: SupportTicket; onClose: () => void }) {
  const { toast } = useToast();
  const {
    messages,
    loading,
    sending,
    isAdminTyping,
    sendMessage,
    sendTypingIndicator,
  } = useTicketChat(ticket.ticket_id);

  const [newMessage, setNewMessage] = useState("");
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Handle typing indicator
  const handleTyping = useCallback(() => {
    sendTypingIndicator(true);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      sendTypingIndicator(false);
    }, 2000);
  }, [sendTypingIndicator]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !selectedImage) return;

    try {
      await sendMessage(newMessage, selectedImage || undefined);
      setNewMessage("");
      setSelectedImage(null);
      setImagePreview(null);
      sendTypingIndicator(false);
    } catch {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    console.log('[TicketChat] File selected:', file?.name, file?.type, file?.size);
    if (!file) return;

    // Validate
    if (!file.type.startsWith("image/")) {
      console.warn('[TicketChat] Invalid file type:', file.type);
      toast({ title: "Error", description: "Please select an image file", variant: "destructive" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Error", description: "Image must be less than 5MB", variant: "destructive" });
      return;
    }

    try {
      setUploading(true);
      console.log('[TicketChat] Starting upload for ticket:', ticket.ticket_id);

      // Convert to base64 and create preview simultaneously
      const base64 = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => {
          const result = r.result as string;
          setImagePreview(result);
          resolve(result.split(",")[1]);
        };
        r.onerror = (err) => reject(new Error('Failed to read file: ' + err));
        r.readAsDataURL(file);
      });

      console.log('[TicketChat] File read complete, uploading to server...');

      const response = await apiClient.post<{ ok: boolean; url?: string; error?: string }>(
        "/support/upload-image",
        {
          image_base64: base64,
          filename: file.name,
          content_type: file.type || 'image/jpeg',
          ticket_id: ticket.ticket_id,
        }
      );

      console.log('[TicketChat] Upload response:', response);

      if (!response.ok || !response.url) {
        throw new Error(response.error || "Upload failed");
      }

      setSelectedImage(response.url);
    } catch (err: any) {
      console.error('[TicketChat] Image upload error:', err);
      toast({ title: "Error", description: err?.message || "Failed to upload image", variant: "destructive" });
      setImagePreview(null);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Card className="flex flex-col h-[calc(100vh-220px)] min-h-[500px]">
      {/* Header */}
      <CardHeader className="border-b flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono text-muted-foreground">
                {ticket.ticket_id}
              </span>
              <Badge className={STATUS_STYLES[ticket.status]}>
                {ticket.status}
              </Badge>
            </div>
            <CardTitle className="text-lg">{ticket.subject}</CardTitle>
          </div>
        </div>
      </CardHeader>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4 max-w-2xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={cn(
                    "flex gap-3 items-end",
                    !msg.from_admin ? "flex-row-reverse" : ""
                  )}
                >
                  {/* Avatar */}
                  <div className={cn(
                    "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm",
                    msg.from_admin
                      ? "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                      : "bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200"
                  )}>
                    {msg.from_admin ? "A" : "U"}
                  </div>

                  <div className="flex flex-col gap-1 max-w-[70%]">
                    {/* Admin label */}
                    {msg.from_admin && (
                      <span className="text-xs font-medium text-muted-foreground ml-1">
                        Support Team
                      </span>
                    )}

                    {/* Message bubble */}
                    <div
                      className={cn(
                        "rounded-2xl px-4 py-2.5 shadow-sm",
                        !msg.from_admin
                          ? "bg-[#94ACF8] text-white rounded-br-md"
                          : "bg-[#F3F4F6] dark:bg-[#374151] text-foreground rounded-bl-md border border-black/5 dark:border-white/5"
                      )}
                    >
                      {msg.image_url && (
                        <img
                          src={msg.image_url}
                          alt="Attachment"
                          className="max-w-full rounded-lg mb-2 cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => setFullScreenImage(msg.image_url)}
                        />
                      )}
                      <p className="whitespace-pre-wrap text-sm leading-relaxed">{msg.content}</p>
                      <p
                        className={cn(
                          "text-xs mt-1.5",
                          !msg.from_admin
                            ? "text-white/70"
                            : "text-muted-foreground"
                        )}
                      >
                        {format(new Date(msg.created_at), "h:mm a")}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {/* Typing indicator */}
              {isAdminTyping && (
                <div className="flex gap-3 items-end">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 flex items-center justify-center font-semibold text-sm">
                    A
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-muted-foreground ml-1">
                      Support Team
                    </span>
                    <div className="bg-muted/80 rounded-2xl rounded-bl-md px-4 py-3 border border-border/50">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>
      </ScrollArea>

      {/* Image Preview */}
      {imagePreview && (
        <div className="px-4 py-2 border-t">
          <div className="relative inline-block">
            <img src={imagePreview} alt="Preview" className="h-20 rounded" />
            {uploading ? (
              <div className="absolute inset-0 bg-black/50 rounded flex items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin text-white" />
              </div>
            ) : (
              <button
                onClick={removeImage}
                className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="border-t p-4 flex-shrink-0">
        <div className="flex gap-2 max-w-2xl mx-auto">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageSelect}
          />
          <Button
            variant="outline"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ImageIcon className="h-4 w-4" />
            )}
          </Button>
          <Textarea
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value);
              handleTyping();
            }}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            className="min-h-[44px] max-h-[120px] resize-none"
            rows={1}
          />
          <Button
            onClick={handleSendMessage}
            disabled={sending || (!newMessage.trim() && !selectedImage)}
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Full-Screen Image Viewer */}
      {fullScreenImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setFullScreenImage(null)}
        >
          <button
            onClick={() => setFullScreenImage(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
          >
            <X className="h-6 w-6 text-white" />
          </button>
          <img
            src={fullScreenImage}
            alt="Full screen"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </Card>
  );
}
